import React, { useEffect, useRef, useState } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../api/api";

export default function ChatOperacion() {
  const location = useLocation();
  const nav = useNavigate();
  const chatRef = useRef(null);
  const emailFinanzasDirtyRef = useRef(false);

  const operacionId = location.state?.operacionId;
  const usuario = JSON.parse(localStorage.getItem("usuario"));

  const [data, setData] = useState(null);
  const [tiempoRestante, setTiempoRestante] = useState("");
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [comprobante, setComprobante] = useState(null);
  const [emailFinanzasActual, setEmailFinanzasActual] = useState("");
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);
  const [confirmandoVenta, setConfirmandoVenta] = useState(false);
  const [mensajeEstado, setMensajeEstado] = useState("");

  useEffect(() => {
    if (!operacionId) {
      alert("Error: No se recibió un ID de operación.");
      nav("/");
    }
  }, [nav, operacionId]);

  const cargarChat = async () => {
    if (!Number.isInteger(Number(operacionId))) {
      console.error("[chat] operacionId inválido", operacionId, location.state);
      alert("Operación inválida");
      nav("/");
      return;
    }

    try {
      const res = await API.get(`/chat/${operacionId}/`);
      setData(res.data);
      const backendEmail =
        res.data?.operacion?.email_finanzas_receptor_actual || "";

      if (backendEmail) {
        emailFinanzasDirtyRef.current = false;
        setEmailFinanzasActual(backendEmail);
      } else if (!emailFinanzasDirtyRef.current) {
        setEmailFinanzasActual("");
      }

      setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
      }, 50);
    } catch (err) {
      console.error("Error cargando chat:", err);
    }
  };

  useEffect(() => {
    if (!operacionId) return;

    cargarChat();
    const interval = setInterval(cargarChat, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operacionId]);

  useEffect(() => {
    if (!data?.operacion) return;

    const fechaLimite = new Date(data.operacion.fecha_limite).getTime();

    const timer = setInterval(() => {
      const diff = fechaLimite - Date.now();

      if (diff <= 0) {
        setTiempoRestante("00:00");
        clearInterval(timer);
        return;
      }

      const min = Math.floor(diff / 60000);
      const seg = Math.floor((diff % 60000) / 1000);

      setTiempoRestante(
        `${String(min).padStart(2, "0")}:${String(seg).padStart(2, "0")}`
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [data]);

  const enviarMensaje = async () => {
    if (!nuevoMensaje.trim()) return;

    try {
      await API.post(`/chat/${operacionId}/enviar/`, {
        contenido: nuevoMensaje,
      });

      setNuevoMensaje("");
      cargarChat();
    } catch (err) {
      console.error("Error enviando mensaje:", err);
    }
  };

  const subirComprobante = async () => {
    if (!comprobante) {
      setMensajeEstado("Seleccione una imagen o archivo del comprobante.");
      return;
    }

    if (!emailFinanzasActual.trim()) {
      setMensajeEstado("Ingrese el email financiero del receptor actual.");
      return;
    }

    try {
      setSubiendoComprobante(true);
      setMensajeEstado("");

      const formData = new FormData();
      formData.append("comprobante", comprobante);
      formData.append(
        "email_finanzas_receptor_actual",
        emailFinanzasActual.trim()
      );

      await API.post(`/chat/${operacionId}/comprobante/`, formData);
      setComprobante(null);
      emailFinanzasDirtyRef.current = false;
      setMensajeEstado("Comprobante cargado correctamente.");
      cargarChat();
    } catch (err) {
      console.error("Error subiendo comprobante:", err);
      setMensajeEstado(
        err.response?.data?.error || "No se pudo subir el comprobante."
      );
    } finally {
      setSubiendoComprobante(false);
    }
  };

  const confirmarVenta = async () => {
    try {
      setConfirmandoVenta(true);
      setMensajeEstado("");

      const res = await API.post(`/operacion/${operacionId}/confirmar/`);
      setData((prev) => ({ ...prev, operacion: res.data }));

      if (res.data.estado_backoffice === "PENDIENTE_REVISION") {
        setMensajeEstado(
          "Ambas partes confirmaron. La operación fue enviada a backoffice."
        );
      } else {
        setMensajeEstado("Tu confirmación fue registrada.");
      }
    } catch (err) {
      console.error("Error confirmando venta:", err);
      setMensajeEstado(
        err.response?.data?.error || "No se pudo confirmar la venta."
      );
    } finally {
      setConfirmandoVenta(false);
    }
  };

  if (!data) return <p className="mt-10 text-center">Cargando chat…</p>;

  const { operacion, mensajes, cliente, comprador } = data;
  const montoOfertado = operacion?.oferta?.monto;
  const esComprador = usuario?.role === "comprador";
  const usuarioYaConfirmo = esComprador
    ? operacion.comprador_acepto_venta
    : operacion.vendedor_acepto_venta;
  const puedeConfirmar =
    operacion.estado === "PENDIENTE" &&
    operacion.estado_backoffice !== "APROBADA" &&
    !usuarioYaConfirmo;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-primary">Chat de operación</h1>

      <Card>
        <h2 className="font-semibold text-lg text-primary mb-3">
          {operacion.publicacion?.titulo}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <p><b>Monto oferta:</b> {Number(montoOfertado || 0).toLocaleString("es-PY")} Gs</p>
          <p><b>Cliente:</b> {cliente?.email}</p>
          <p><b>Comprador:</b> {comprador?.email}</p>
          <p><b>Estado operación:</b> {operacion.estado?.toUpperCase() || "N/A"}</p>
          <p><b>Backoffice:</b> {operacion.estado_backoffice}</p>
          <p><b>Tiempo restante:</b> {tiempoRestante}</p>
        </div>

        {operacion.publicacion?.factura_data && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <p><b>CDC:</b> {operacion.publicacion.factura_data.cdc}</p>
            <p><b>Email finanzas emisor original:</b> {operacion.publicacion.factura_data.email_finanzas_emisor_original || "-"}</p>
            <p><b>Email finanzas receptor original:</b> {operacion.publicacion.factura_data.email_finanzas_receptor_original || "-"}</p>
            <p><b>Email finanzas receptor actual:</b> {operacion.email_finanzas_receptor_actual || "-"}</p>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <span className={`px-3 py-1 rounded-full ${operacion.comprador_acepto_venta ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            Comprador {operacion.comprador_acepto_venta ? "confirmó" : "pendiente"}
          </span>
          <span className={`px-3 py-1 rounded-full ${operacion.vendedor_acepto_venta ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            Vendedor {operacion.vendedor_acepto_venta ? "confirmó" : "pendiente"}
          </span>
        </div>

        {operacion.comprobante_transferencia_url && (
          <p className="mt-4 text-sm">
            <b>Comprobante:</b>{" "}
            <a
              href={operacion.comprobante_transferencia_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              Ver archivo adjunto
            </a>
          </p>
        )}
      </Card>

      {mensajeEstado && (
        <Card className="border border-blue-200 bg-blue-50 text-blue-800">
          {mensajeEstado}
        </Card>
      )}

      <Card>
        <h3 className="font-semibold text-primary mb-3">Mensajes</h3>

        <div
          ref={chatRef}
            className="border rounded-lg h-64 p-3 overflow-auto bg-cream/50 space-y-2"
        >
          {mensajes.map((msg) => {
            const soyYo = Number(msg.remitente) === Number(usuario.id);

            return (
              <div
                key={msg.id}
                className={`flex ${soyYo ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`px-3 py-2 rounded-lg max-w-xs ${
                    msg.remitente === "sistema"
                      ? "bg-yellow-200 text-slate-700"
                      : soyYo
                        ? "bg-primary text-white"
                        : "bg-slate-200 text-slate-700"
                  }`}
                >
                  <p>{msg.contenido}</p>
                  <span className="text-[10px] opacity-70 block mt-1">
                    {new Date(msg.fecha).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {operacion.estado !== "PAGADO" && operacion.estado !== "FALLIDA" && (
          <div className="flex gap-2 mt-3">
            <input
              className="flex-1 border rounded p-2"
              placeholder="Escriba un mensaje…"
              value={nuevoMensaje}
              onChange={(e) => setNuevoMensaje(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && enviarMensaje()}
            />
            <Button onClick={enviarMensaje}>Enviar</Button>
          </div>
        )}
      </Card>

      {esComprador && operacion.estado === "PENDIENTE" && (
        <Card className="space-y-3">
          <h3 className="font-semibold text-primary">
            Cargar comprobante de transferencia
          </h3>
          <input
            type="email"
            className="border rounded p-2 w-full"
            placeholder="Email financiero del receptor actual"
            value={emailFinanzasActual}
            onChange={(e) => {
              emailFinanzasDirtyRef.current = true;
              setEmailFinanzasActual(e.target.value);
            }}
          />
          <input
            type="file"
            accept="image/*,.pdf"
            className="block w-full text-sm"
            onChange={(e) => setComprobante(e.target.files?.[0] || null)}
          />
          <Button disabled={subiendoComprobante} onClick={subirComprobante}>
            {subiendoComprobante ? "Subiendo..." : "Subir comprobante"}
          </Button>
        </Card>
      )}

      {puedeConfirmar && (
        <Button
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          disabled={
            confirmandoVenta ||
            (esComprador &&
              (!operacion.comprobante_transferencia_url ||
                !operacion.email_finanzas_receptor_actual))
          }
          onClick={confirmarVenta}
        >
          {confirmandoVenta ? "Confirmando..." : "Aceptar la venta"}
        </Button>
      )}

      {operacion.estado_backoffice === "PENDIENTE_REVISION" && (
        <Card className="border border-amber-200 bg-amber-50 text-amber-800">
          La operación está pendiente de aprobación por backoffice.
        </Card>
      )}

      {operacion.estado_backoffice === "APROBADA" && (
        <Card className="border border-emerald-200 bg-emerald-50 text-emerald-800">
          El backoffice aprobó la venta y el contrato fue gestionado.
        </Card>
      )}
    </div>
  );
}
